import { View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components";

import { TestAppState } from "../../../app";
import { CriticalFindingCard } from "../../../components/findings/critical-finding-card";
import {
  DistributionChannelOption,
  RiskLevel,
  StrideThreat,
} from "../../../components/findings/risk-heat-map-card";
import { RiskHeatMapCard } from "../../../components/findings/risk-heat-map-card";

/**
 * Fixed STRIDE threat categories shown on the Risk Heat Map. These are never
 * tenant-configurable (#2413).
 */
export const STRIDE_THREATS: StrideThreat[] = [
  {
    key: "spoofing",
    label: "Spoofing",
    description: "impersonating an identity",
  },
  {
    key: "tampering",
    label: "Tampering",
    description: "unauthorised data change",
  },
  {
    key: "repudiation",
    label: "Repudiation",
    description: "denying an action occurred",
  },
  {
    key: "infoDisclosure",
    label: "Info disclosure",
    description: "exposing sensitive data",
  },
  {
    key: "denialOfService",
    label: "Denial of service",
    description: "disrupting availability",
  },
  {
    key: "elevationOfPrivilege",
    label: "Elevation of privilege",
    description: "gaining unauthorised access",
  },
];

const RISK_LEVEL_CYCLE: RiskLevel[] = ["Low", "Medium", "High"];

/**
 * Deterministically cycles Low/Medium/High across the heat map grid so the
 * illustrative data looks varied without depending on specific channel or
 * threat identities (#2413).
 */
export function generateIllustrativeCellData(
  threats: StrideThreat[],
  channels: DistributionChannelOption[]
): Record<string, Record<string, RiskLevel>> {
  const cellData: Record<string, Record<string, RiskLevel>> = {};

  threats.forEach((threat, threatIndex) => {
    cellData[threat.key] = {};
    channels.forEach((channel, channelIndex) => {
      cellData[threat.key][channel.id] =
        RISK_LEVEL_CYCLE[
          (threatIndex + channelIndex) % RISK_LEVEL_CYCLE.length
        ];
    });
  });

  return cellData;
}

/** Adds the Findings page's Risk Heat Map and Critical Finding cards (#2413). */
export function addFindingsCards(view: View<TestAppState, DragAndDropLayout>) {
  const riskHeatMapComponent = view.addComponent({
    component: RiskHeatMapCard,
    layoutProps: {
      componentId: "riskHeatMapComponent",
      labels: ["findings", "stride", "riskHeatMap"],
      description: "Illustrative STRIDE risk levels per distribution channel.",
      title: "Risk Heat Map",
    },
  });

  riskHeatMapComponent.mapState(({ settings }) => {
    const channels: DistributionChannelOption[] =
      settings?.configuration?.distributionChannelDefinition ?? [];

    return {
      title: "Risk Heat Map",
      threats: STRIDE_THREATS,
      channels,
      cellData: generateIllustrativeCellData(STRIDE_THREATS, channels),
    };
  });

  const criticalFindingComponent = view.addComponent({
    component: CriticalFindingCard,
    layoutProps: {
      componentId: "criticalFindingComponent",
      labels: ["findings", "stride", "criticalFinding"],
      description: "Illustrative critical finding of the week.",
      title: "Critical finding of the week",
    },
  });

  criticalFindingComponent.mapState(() => ({
    title: "Critical finding of the week",
    headline: "Unrestricted outbound access from a legacy system",
    finding:
      "A placeholder finding describing an illustrative security issue for this distribution channel. Real findings will be sourced from the SRLabs integration (#2416).",
    rationale:
      "A placeholder rationale explaining why this illustrative issue would matter for the tenant's risk posture.",
    remediation:
      "A placeholder remediation recommendation describing the illustrative next step to reduce the risk.",
  }));

  return { riskHeatMapComponent, criticalFindingComponent };
}
